import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

function BriefingEditor({ type, label }) {
  const [briefing, setBriefing] = useState(null);
  const [form, setForm] = useState({ title: "", content_html: "", video_url: "", pdf_url: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/briefings/active/${type}`);
      setBriefing(data);
      setForm({ title: data.title, content_html: data.content_html, video_url: data.video_url || "", pdf_url: data.pdf_url || "" });
    } catch {}
  };

  useEffect(() => {
    load();
  }, [type]);

  const save = async () => {
    await api.post("/briefings", { briefing_type: type, ...form });
    toast.success(`${label} updated — visitors will be asked to re-sign`);
    load();
  };

  return (
    <div className="border border-slate-200 bg-white p-6 space-y-4" data-testid={`briefing-editor-${type}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold uppercase text-brand-navy">{label}</h3>
        {briefing && <span className="text-xs uppercase tracking-widest text-slate-400 border border-slate-300 px-2 py-1">v{briefing.version}</span>}
      </div>
      <Input
        data-testid={`${type}-title-input`}
        placeholder="Title"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="rounded-sm bg-white border-slate-300"
      />
      <Textarea
        data-testid={`${type}-content-input`}
        placeholder="Briefing content (HTML supported)"
        rows={8}
        value={form.content_html}
        onChange={(e) => setForm({ ...form, content_html: e.target.value })}
        className="rounded-sm bg-white border-slate-300"
      />
      <p className="text-xs text-slate-400 -mt-2">
        Wrap rules in <code className="font-mono">&lt;ol class="briefing-list"&gt;&lt;li&gt;...&lt;/li&gt;&lt;/ol&gt;</code> for numbered rows,
        add <code className="font-mono">class="critical"</code> on a <code className="font-mono">&lt;li&gt;</code> for an amber warning number,
        use <code className="font-mono">&lt;strong&gt;</code> to bold key phrases, and close with{" "}
        <code className="font-mono">&lt;p class="ack-note"&gt;...&lt;/p&gt;</code> for the acknowledgment box.
      </p>
      <Input
        data-testid={`${type}-video-input`}
        placeholder="Video URL (optional)"
        value={form.video_url}
        onChange={(e) => setForm({ ...form, video_url: e.target.value })}
        className="rounded-sm bg-white border-slate-300"
      />
      <Input
        data-testid={`${type}-pdf-input`}
        placeholder="PDF URL (optional)"
        value={form.pdf_url}
        onChange={(e) => setForm({ ...form, pdf_url: e.target.value })}
        className="rounded-sm bg-white border-slate-300"
      />
      <Button onClick={save} data-testid={`${type}-save-button`} className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm">
        Save New Version
      </Button>
    </div>
  );
}

export default function Briefings() {
  return (
    <div className="p-8 space-y-6" data-testid="briefings-page">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Compliance</span>
        <h1 className="text-3xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Briefing Content</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BriefingEditor type="safety" label="Safety Briefing" />
        <BriefingEditor type="ctpat" label="CTPAT Briefing" />
      </div>
    </div>
  );
}
